import { NgClass, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { RouterLink } from '@angular/router';

import {
  ButtonComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  PlaceloaderComponent,
} from '@core/user-interface-legacy';

import type { ProductUpdate } from '../../models/product-update.model';
import {
  MainTab,
  ProductTab,
  ProductUpdatesStateService,
} from '../../services/product-updates-state.service';
import { ThemeService } from '../../services/theme.service';
import { TranslationService } from '../../services/translation.service';
import { CategoryBadgeComponent } from '../category-badge/category-badge.component';
import { MobileMenuComponent } from '../mobile-menu/mobile-menu.component';

type StickyState = 'hidden' | 'visible' | 'hiding';
export type HeaderMode = 'feed' | 'article';

@Component({
  selector: 'pu-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgClass,
    NgTemplateOutlet,
    RouterLink,
    ButtonComponent,
    HeadingComponent,
    IconButtonComponent,
    IconComponent,
    PlaceloaderComponent,
    CategoryBadgeComponent,
    MobileMenuComponent,
  ],
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.less'],
})
export class HeaderComponent {
  private readonly themeService = inject(ThemeService);

  public readonly translationService = inject(TranslationService);
  public readonly stateService = inject(ProductUpdatesStateService);

  public readonly mode = input<HeaderMode>('feed');
  public readonly article = input<ProductUpdate | null>(null);
  public readonly activeMainTab = input<MainTab>('whats-new');
  public readonly activeProductTab = input<ProductTab>('all');
  public readonly stickyState = input<StickyState>('hidden');

  public readonly mainTabChange = output<MainTab>();
  public readonly productTabChange = output<ProductTab>();

  public readonly isDarkMode = this.themeService.isDarkMode;
  public readonly isLanguageDropdownOpen = signal(false);
  public readonly isMobileMenuOpen = signal(false);

  public readonly productTabs: Array<{ id: ProductTab; label: string }> = [
    { id: <ProductTab>'all', label: 'All' },
    { id: <ProductTab>'ai', label: 'AI' },
    { id: <ProductTab>'hire', label: 'Hire' },
    { id: <ProductTab>'manage', label: 'Manage' },
    { id: <ProductTab>'grow', label: 'Grow' },
  ];

  public onMainTabChange(tab: MainTab): void {
    this.mainTabChange.emit(tab);
  }

  public onProductTabChange(tab: ProductTab): void {
    this.productTabChange.emit(tab);
  }

  public toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  public toggleLanguageDropdown(): void {
    this.isLanguageDropdownOpen.update(current => !current);
  }

  public selectLanguage(locale: string): void {
    this.translationService.setLocale(locale);
    this.stateService.setLocale(locale);
    this.isLanguageDropdownOpen.set(false);
  }

  public getLanguageLabel(locale: string): string {
    const labels: Record<string, string> = {
      en: 'English',
      de: 'Deutsch',
      fr: 'Français',
      nl: 'Nederlands',
      fi: 'Suomi',
      sv: 'Svenska',
    };
    return labels[locale] ?? locale.toUpperCase();
  }

  public getActiveMainTabLabel(): string {
    const tab = this.activeMainTab();
    switch (tab) {
      case 'whats-new':
        return this.translationService.translate('nav.whats_new');
      case 'coming-soon':
        return this.translationService.translate('nav.coming_soon');
      case 'timeline':
        return this.translationService.translate('nav.timeline');
      case 'changelog':
        return this.translationService.translate('nav.changelog');
      default:
        return '';
    }
  }

  public toggleMobileMenu(): void {
    this.isMobileMenuOpen.update(open => !open);
  }

  public closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  public onMobileMainTabChange(tab: MainTab): void {
    this.mainTabChange.emit(tab);
    this.closeMobileMenu();
  }

  public onMobileProductTabChange(tab: ProductTab): void {
    this.productTabChange.emit(tab);
    this.closeMobileMenu();
  }
}
